import type { ReactNode } from 'react'
import React, { forwardRef } from 'react'

/**
 * Start Helper Types for Polymorphic Components that Forward their Ref
 * @see {@link https://www.benmvp.com/blog/forwarding-refs-polymorphic-react-component-typescript/}
 */

export type PropsOf<
  C extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<unknown>,
> = JSX.LibraryManagedAttributes<C, React.ComponentPropsWithoutRef<C>>

interface TagProp<C extends React.ElementType> {
  /**
   * An override of the default HTML tag.
   * Can also be another React component.
   */
  tag?: C
}

export type ExtendableProps<
  ExtendedProps = Record<string, unknown>,
  OverrideProps = Record<string, unknown>,
> = OverrideProps & Omit<ExtendedProps, keyof OverrideProps>

export type InheritableElementProps<
  C extends React.ElementType,
  Props = Record<string, unknown>,
> = ExtendableProps<PropsOf<C>, Props>

export type PolymorphicComponentProps<
  C extends React.ElementType,
  Props = Record<string, unknown>,
> = InheritableElementProps<C, Props & TagProp<C>>

export type PolymorphicRef<C extends React.ElementType> =
  React.ComponentPropsWithRef<C>['ref']

export type PolymorphicComponentPropsWithRef<
  C extends React.ElementType,
  Props = Record<string, unknown>,
> = PolymorphicComponentProps<C, Props> & { ref?: PolymorphicRef<C> }

/**
 * Outline Button Component
 */

export const outlineButtonStyles =
  'px-3 py-2 leading-none inline-block text-center rounded border border-blog-oceany-blue-300 text-blog-oceany-blue-300 font-bold'

interface Props {
  children: ReactNode
}

type OutlineButtonProps<C extends React.ElementType> =
  PolymorphicComponentPropsWithRef<C, Props>

type OutlineButtonComponent = <C extends React.ElementType = 'span'>(
  props: OutlineButtonProps<C>,
) => React.ReactNode | null

export const OutlineButton: OutlineButtonComponent = forwardRef(
  /* @ts-expect-error - TypeScript struggles with inference here */
  // TODO: Fix the TypeScript error when using `forwardRef` with `PolymorphicComponentProps`
  function OutlineButtonDisplayName<C extends React.ElementType = 'span'>(
    { tag, children, ...props }: OutlineButtonProps<C>,
    ref: PolymorphicRef<C>,
  ) {
    const Component = tag ?? 'span'
    return (
      <Component ref={ref} className={outlineButtonStyles} {...props}>
        {children}
      </Component>
    )
  },
)
